import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Checkbox,
  Radio,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  styled,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DELETEICON from "../../assets/icons/delete.png";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 44,
  height: 22,
  padding: 0,
  // margin: '0 8px 0 0',
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#9048FB",
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 18,
    height: 18,
  },
  "& .MuiSwitch-track": {
    borderRadius: 16,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const optionSchema = yup.object().shape({
  id: yup.string().required(),
  text: yup.string().required("Option text is required"),
});

const questionSchema = yup.object().shape({
  text: yup.string().required("Question text is required"),
  category: yup
    .string()
    .oneOf(["text", "multipleChoice"])
    .required("Category is required"),
  required: yup.boolean(),
  selectionType: yup.string().when("category", {
    is: "multipleChoice",
    then: (schema) =>
      schema
        .oneOf(["single", "multiple"])
        .required("Selection type is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  options: yup.array().when("category", {
    is: "multipleChoice",
    then: (schema) =>
      schema
        .of(optionSchema)
        .min(2, "At least two options are required")
        .required("Options are required"),
    otherwise: (schema) => schema.notRequired(),
  }),

  // selectedOption: yup.mixed().when(["category", "required", "selectionType"], {
  //   is: (category, required, selectionType) => category === "multipleChoice",
  //   then: (schema) =>
  //     schema.nullable().test({
  //       name: "at-least-one-selected",
  //       message: "Please select at least one option", // Clear message
  //       test: (value) => {
  //         if (value === null || value === undefined) return false;
  //         if (Array.isArray(value)) return value.length > 0;
  //         return true; // For single select (radio), value is a number
  //       },
  //     }),
  //   otherwise: (schema) => schema.notRequired(),
  // }),
});

const schema = yup.object().shape({
  useAI: yup.boolean(),
  form_name: yup
    .string()
    .required("Form name is required")
    .min(2, "Form name must be at least 2 characters")
    .max(100, "Form name cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9\s\-_()]+$/,
      "Form name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses"
    )
    .test(
      "no-only-spaces",
      "Form name cannot contain only spaces",
      (value) => value && value.trim().length > 0
    ),
  questions: yup
    .array()
    .of(questionSchema)
    .min(1, "At least one question is required"),
});

const PreAssessmentForm = ({
  open,
  onClose,
  onSave,
  onRemove,
  user,
  initialData = null,
  formAttached,
  loaderForForm,
}) => {
  const [useAI, setUseAI] = useState(initialData?.useAI || true);
  const [isEditingFormName, setIsEditingFormName] = useState(false);

  // Default form values if no initial data is provided
  const defaultValues = {
    useAI: true,
    form_name: "Untitled form",
    questions: [
      {
        id: 1,
        text: "",
        category: "text",
        required: false,
        options: [],
        selectionType: "single",
        selectedOption: null,
      },
    ],
  };
  console.log("initialData", initialData);

  // Merge initial data with default values if provided
  const formDefaultValues = initialData ? initialData : defaultValues;

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "questions",
  });

  const questions = watch("questions");

  const handleAddTextQuestion = () => {
    const newId =
      questions.length > 0 ? Math.max(...questions.map((q) => q.id)) + 1 : 1;
    append({
      id: newId,
      text: "",
      category: "text",
      required: false,
      options: [],
      selectionType: "single",
    });
  };

  const handleAddMultipleChoiceQuestion = () => {
    const newId =
      questions.length > 0 ? Math.max(...questions.map((q) => q.id)) + 1 : 1;
    append({
      id: newId,
      text: "",
      category: "multipleChoice",
      required: false,
      options: [
        { id: 1, text: "Option 1" },
        { id: 2, text: "Option 2" },
      ],
      selectionType: "single",
      selectedOption: null,
    });
  };

  const handleAddOption = (questionIndex) => {
    const currentQuestion = questions[questionIndex];
    const newOptions = [...currentQuestion.options];
    const newOptionId =
      newOptions.length > 0
        ? Math.max(...newOptions.map((opt) => opt.id)) + 1
        : 1;

    newOptions.push({
      id: newOptionId,
      text: `Option ${newOptions.length + 1}`,
    });

    const updatedQuestion = {
      ...currentQuestion,
      options: newOptions,
    };

    update(questionIndex, updatedQuestion);
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    const currentQuestion = questions[questionIndex];
    const newOptions = [...currentQuestion.options];
    newOptions.splice(optionIndex, 1);

    const updatedQuestion = {
      ...currentQuestion,
      options: newOptions,
    };

    update(questionIndex, updatedQuestion);
  };

  const handleSelectionTypeChange = (questionIndex, value) => {
    const currentQuestion = questions[questionIndex];

    const updatedQuestion = {
      ...currentQuestion,
      selectionType: value,
      selectedOption:
        value === "single"
          ? null
          : currentQuestion.selectedOption
          ? [currentQuestion.selectedOption] // convert to array if previously single
          : [], // or start fresh
    };

    update(questionIndex, updatedQuestion);
  };

  const handleSaveAndContinue = handleSubmit(
    (data) => {
      // Make sure we have a clean data structure with proper values
      const formattedData = {
        useAI: data.useAI,
        form_name: data?.form_name,
        questions: data.questions.map((question) => ({
          ...question,
          ...(question.category === "text" ? { options: [] } : {}),
        })),
      };

      console.log("Form data submitted:", formattedData);
      onSave?.(formattedData);
    },
    (errors) => {
      console.error("Form validation errors:", errors);
    }
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          width: { xs: "95vw", md: "60vw" },
          height: { xs: "95vh", md: "75vh" },
          overflow: "auto",
          maxWidth: "none",
          maxHeight: "none",
          p: 2,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            color="#9048FB"
            fontWeight={600}
            fontFamily="Outfit"
          >
            Create Pre-Assessment Form
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ border: 1, borderColor: "#999", borderRadius: "50%" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              borderRadius: "8px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
              bgcolor: "white",
              width: "100%",
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              sx={{
                borderTop: "4px solid #9048FB",
                borderRadius: "8px 8px 0 0",
                p: 3,
                pb: 2,
              }}
            >
              <Controller
                name="form_name"
                control={control}
                render={({ field }) => (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        {...field}
                        variant="standard"
                        // fullWidth
                        error={Boolean(errors.form_name)}
                        InputProps={{
                          readOnly: !isEditingFormName,
                        }}
                        sx={{
                          // width: "auto",
                          "& .MuiInput-underline:before": {
                            borderBottom: errors.form_name
                              ? "1px solid #d32f2f" // red if error
                              : isEditingFormName
                              ? "1px solid #e0e0e0"
                              : "none",
                          },
                          "& .MuiInput-underline:hover:before": {
                            borderBottom: errors.form_name
                              ? "1px solid #d32f2f !important"
                              : isEditingFormName
                              ? "1px solid #9048FB !important"
                              : "none !important",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottom: errors.form_name
                              ? "2px solid #d32f2f" // red if error
                              : isEditingFormName
                              ? "2px solid #9048FB"
                              : "none",
                          },
                          "& .MuiInputBase-input": {
                            fontFamily: "Outfit",
                            fontSize: "1.25rem",
                            fontWeight: 600,
                            color: "#333",
                            padding: "2px 0",
                            backgroundColor: "transparent",
                            cursor: isEditingFormName ? "text" : "default",
                          },
                          "& .MuiInputBase-input:hover": {
                            backgroundColor: isEditingFormName
                              ? "rgba(144, 72, 251, 0.02)"
                              : "transparent",
                          },
                          "& .MuiInputBase-input:focus": {
                            backgroundColor: "rgba(144, 72, 251, 0.02)",
                          },
                        }}
                        onBlur={(e) => {
                          field.onBlur(e);
                          trigger("form_name"); // Trigger validation when field is blurred
                          // Auto-save when user clicks away
                          if (isEditingFormName) {
                            setIsEditingFormName(false);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && isEditingFormName) {
                            trigger("form_name"); // Trigger validation before closing edit mode

                            setIsEditingFormName(false);
                            e.target.blur();
                          }
                          if (e.key === "Escape" && isEditingFormName) {
                            setIsEditingFormName(false);
                            e.target.blur();
                          }
                        }}
                      />
                      <Box
                        onClick={() => {
                          if (isEditingFormName) {
                            setIsEditingFormName(false);
                          } else {
                            setIsEditingFormName(true);
                            setTimeout(() => {
                              const input = document.querySelector(
                                'input[name="form_name"]'
                              );
                              if (input) {
                                input.focus();
                                input.select();
                              }
                            }, 100);
                          }
                        }}
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: isEditingFormName
                            ? "#e8f5e8"
                            : "#f0f0f0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: isEditingFormName
                              ? "#d4edda"
                              : "#e0e0e0",
                            transform: "scale(1.05)",
                          },
                        }}
                      >
                        {isEditingFormName ? (
                          <CheckIcon
                            sx={{
                              color: "#28a745",
                              fontSize: "12px",
                            }}
                          />
                        ) : (
                          <EditIcon
                            sx={{
                              color: "#666",
                              fontSize: "12px",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                    {errors.form_name && (
                      <Typography
                        color="#d32f2f"
                        variant="caption"
                        sx={{ mb: 2 }}
                      >
                        {errors.form_name.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>

            <Divider sx={{ width: "100%" }} />

            <Box
              sx={{ display: "flex", alignItems: "center", p: 2, py: 2, ml: 1 }}
            >
              <Controller
                name="useAI"
                control={control}
                render={({ field }) => (
                  <IOSSwitch
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      setUseAI(e.target.checked);
                    }}
                  />
                )}
              />

              <Box ml={2}>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  fontFamily="Outfit"
                >
                  Use this data to inform AI interviewer
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontFamily="Outfit"
                >
                  Enable AI to ask more relevant questions based on responses
                </Typography>
              </Box>
            </Box>
          </Box>

          {errors.questions && !Array.isArray(errors.questions) && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errors.questions.message}
            </Typography>
          )}

          {fields.map((field, index) => (
            <Paper
              key={field.id}
              elevation={0}
              sx={{
                mb: 3,
                p: 3,
                borderRadius: 2,
                border: "1px solid #C084FC",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  fontFamily="Outfit"
                >
                  {questions[index].category === "text"
                    ? "Question"
                    : "Multiple Choice Question"}{" "}
                  {index + 1}
                </Typography>
                <Box>
                  <Button
                    variant="contained"
                    size="small"
                    disabled
                    fontFamily="Outfit"
                    sx={{
                      borderRadius: "20px",
                      "&.Mui-disabled": {
                        bgcolor: "#4bb1cf",
                        color: "white",
                      },
                    }}
                  >
                    {questions[index].category === "text"
                      ? "Text"
                      : "Multiple Choice"}
                  </Button>
                </Box>
              </Box>

              <Controller
                name={`questions.${index}.text`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Type question"
                    fullWidth
                    variant="outlined"
                    error={Boolean(errors.questions?.[index]?.text)}
                    helperText={errors.questions?.[index]?.text?.message || ""}
                    sx={{ mb: 2, fontFamily: "Outfit" }}
                  />
                )}
              />

              {questions[index].category === "multipleChoice" && (
                <Box sx={{ pl: 2 }}>
                  {questions[index].options.map((option, optIndex) => (
                    <Box
                      key={option.id}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      {questions[index].selectionType === "single" ? (
                        <Controller
                          name={`questions.${index}.selectedOption`}
                          control={control}
                          render={({ field }) => (
                            <Radio
                              checked={field.value === optIndex}
                              onChange={() => field.onChange(optIndex)}
                              sx={{ p: 0.5 }}
                            />
                          )}
                        />
                      ) : (
                        <Controller
                          name={`questions.${index}.selectedOption`}
                          control={control}
                          render={({ field }) => {
                            const selected = field.value || [];
                            const isChecked = selected.includes(optIndex);

                            const handleChange = () => {
                              const updated = isChecked
                                ? selected.filter((i) => i !== optIndex)
                                : [...selected, optIndex];
                              field.onChange(updated);
                            };

                            return (
                              <Checkbox
                                checked={isChecked}
                                onChange={handleChange}
                                sx={{ p: 0.5 }}
                              />
                            );
                          }}
                        />
                      )}

                      <Controller
                        name={`questions.${index}.options.${optIndex}.text`}
                        control={control}
                        render={({ field }) => (
                          <Box sx={{ width: "100%" }}>
                            <TextField
                              {...field}
                              size="small"
                              fullWidth
                              variant="outlined"
                              error={Boolean(
                                errors.questions?.[index]?.options?.[optIndex]
                                  ?.text
                              )}
                              sx={{ ml: 1, fontFamily: "Outfit" }}
                            />
                            {errors.questions?.[index]?.options?.[optIndex]
                              ?.text && (
                              <FormHelperText error sx={{ ml: 2 }}>
                                {
                                  errors.questions[index].options[optIndex].text
                                    .message
                                }
                              </FormHelperText>
                            )}
                          </Box>
                        )}
                      />
                      {questions[index].options.length > 2 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveOption(index, optIndex)}
                          sx={{ ml: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {errors.questions?.[index]?.selectedOption && (
                    <FormHelperText error sx={{ ml: 5 }}>
                      {errors.questions[index].selectedOption.message}
                    </FormHelperText>
                  )}

                  {errors.questions?.[index]?.options &&
                    !Array.isArray(errors.questions[index].options) && (
                      <FormHelperText error sx={{ ml: 2, mb: 1 }}>
                        {errors.questions[index].options.message}
                      </FormHelperText>
                    )}

                  {questions[index].options.length < 4 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddOption(index)}
                        sx={{ color: "#9048FB", fontFamily: "Outfit" }}
                      >
                        Add Option
                      </Button>
                    </Box>
                  )}

                  <FormControl
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 200, mt: 1 }}
                    error={Boolean(errors.questions?.[index]?.selectionType)}
                  >
                    <Select
                      value={questions[index].selectionType}
                      onChange={(e) =>
                        handleSelectionTypeChange(index, e.target.value)
                      }
                      sx={{ borderRadius: 1, fontFamily: "Outfit" }}
                    >
                      <MenuItem value="single" fontFamily="Outfit">
                        Single selection
                      </MenuItem>
                      <MenuItem value="multiple" fontFamily="Outfit">
                        Multiple selection
                      </MenuItem>
                    </Select>

                    {/* Display helper text based on selection type */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontFamily="Outfit"
                      mt={1}
                    >
                      {questions[index].selectionType === "single"
                        ? "Candidate can select only one option"
                        : "Candidate can select multiple options"}
                    </Typography>

                    {errors.questions?.[index]?.selectionType && (
                      <FormHelperText>
                        {errors.questions[index].selectionType.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  justifyContent: "space-between",
                }}
              >
                <Controller
                  name={`questions.${index}.required`}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          sx={{
                            "&.Mui-checked": {
                              color: "#9048FB",
                            },
                          }}
                        />
                      }
                      label="Required"
                    />
                  )}
                />

                {fields.length > 1 && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => remove(index)}
                    sx={{ ml: 1 }}
                  >
                    <img src={DELETEICON} height={25} alt="Delete" />
                  </IconButton>
                )}
              </Box>
            </Paper>
          ))}

          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddTextQuestion}
              sx={{
                borderColor: "#9048FB",
                color: "#9048FB",
                fontFamily: "Outfit",
                "&:hover": {
                  borderColor: "#7035d8",
                  bgcolor: "rgba(144, 72, 251, 0.04)",
                },
                borderRadius: "4px",
              }}
            >
              Text Question
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddMultipleChoiceQuestion}
              sx={{
                borderColor: "#9048FB",
                color: "#9048FB",
                fontFamily: "Outfit",
                "&:hover": {
                  borderColor: "#7035d8",
                  bgcolor: "rgba(144, 72, 251, 0.04)",
                },
                borderRadius: "4px",
              }}
            >
              Multiple Choice
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 1, display: "flex", justifyContent: "space-between" }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            borderColor: "#d0d0d0",
            color: "#606060",
            borderRadius: "4px",
            fontFamily: "Outfit",
          }}
        >
          Cancel
        </Button>
        <Box
          sx={{
            display: "flex",
            alignItems: " center",
            gap: 2,
          }}
        >
          {formAttached && (
            <Button
              variant="contained"
              onClick={onRemove}
              startIcon={<DeleteOutlineIcon />}
              sx={{
                fontFamily: "Outfit",
                borderRadius: "4px",
                bgcolor: "transparent",
                border: "1px solid #FF2226",
                color: "#FF2226",
              }}
            >
              Remove form
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleSaveAndContinue}
            disabled={loaderForForm}
            sx={{
              bgcolor: "#9048FB",
              "&:hover": { bgcolor: "#7035d8" },
              fontFamily: "Outfit",
              borderRadius: "4px",
              minWidth: 150,
            }}
          >
            {loaderForForm ? (
              <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
            ) : null}
            {loaderForForm ? "Saving..." : "Save and Continue"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

/* Export
  ============================================================================= */
export default PreAssessmentForm;
